import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Building2, Loader2, MapPin, Phone, Mail, User } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import {
  PAGE_WIDE, Tabs, SearchInput, DataTable, Dialog, Field, FormRow, Switch, StatusPill,
  EmptyState, ErrorState, TableSkeleton, num, plural,
} from '../../components/ui';

// Facilities: the locations and sites the organisation works at. A table from lg up (easy to scan
// and compare once there are more than a handful), cards on smaller screens.

const AU_STATES = ['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'];
const FACILITY_TYPES = ['Day Program', 'Residential', 'Respite', 'Group Home', 'Community Centre', 'Hospital', 'Clinic', 'School', 'Workplace', 'Other'];

// '18 Ashwood Grove, Ashwood VIC 3147' — Australian style, no comma between state and postcode.
// A picked address may already carry the suburb, state and postcode; it isn't repeated.
function formatAddress(f) {
  const street = String(f.address || '').trim();
  const locality = [f.suburb, [f.state, f.postcode].filter(Boolean).join(' ')].filter(Boolean).join(' ');
  const tidy = (s) => s.replace(new RegExp(`,\\s*(${AU_STATES.join('|')}),?\\s*(\\d{4})\\b`), ' $1 $2');
  if (!street) return locality;
  if (f.suburb && street.toLowerCase().includes(String(f.suburb).toLowerCase())) return tidy(street);
  return tidy([street, locality].filter(Boolean).join(', '));
}

function FacilityDialog({ facility, onClose, onSaved }) {
  const isEdit = !!(facility && facility.id);
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState('');
  const [form, setForm] = useState(() => ({
    name: (facility && facility.name) || '',
    facilityType: (facility && facility.facility_type) || '',
    address: (facility && facility.address) || '',
    suburb: (facility && facility.suburb) || '',
    state: (facility && facility.state) || 'VIC',
    postcode: (facility && facility.postcode) || '',
    phone: (facility && facility.phone) || '',
    email: (facility && facility.email) || '',
    contactName: (facility && facility.contact_name) || '',
    notes: (facility && facility.notes) || '',
    isActive: !facility || facility.is_active !== false,
  }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!form.name.trim()) { setNameError('Enter the facility name.'); return; }
    setSaving(true);
    try {
      if (isEdit) { await api.put(`/facilities/${facility.id}`, form); toast.success('Facility updated'); }
      else { await api.post('/facilities', form); toast.success('Facility added'); }
      onSaved();
    } catch (err) {
      toast.error((err && err.response && err.response.data && err.response.data.message) || 'Could not save the facility');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onClose={onClose} size="lg" title={isEdit ? `Edit ${facility.name}` : 'Add facility'}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" form="facility-form" disabled={saving} className="btn-primary">
            {saving && <Loader2 size={14} className="animate-spin" />}{isEdit ? 'Save changes' : 'Add facility'}
          </button>
        </>
      )}>
      <form id="facility-form" onSubmit={handleSave} className="space-y-4">
        <FormRow>
          <Field label="Facility name" required error={nameError}>
            {(id) => <input id={id} className="input-field" value={form.name} aria-invalid={!!nameError} onChange={(e) => { set('name', e.target.value); if (nameError) setNameError(''); }} />}
          </Field>
          <Field label="Type">
            {(id) => (
              <select id={id} className="input-field" value={form.facilityType} onChange={(e) => set('facilityType', e.target.value)}>
                <option value="">Choose a type</option>
                {FACILITY_TYPES.map((t) => <option key={t}>{t}</option>)}
                {form.facilityType && !FACILITY_TYPES.includes(form.facilityType) && <option>{form.facilityType}</option>}
              </select>
            )}
          </Field>
        </FormRow>
        <Field label="Street address">
          <AddressAutocomplete className="input-field" value={form.address} onChange={(v) => set('address', v)}
            onSelect={(a) => setForm((f) => ({
              ...f,
              address: [a.street, a.suburb, a.state, a.postcode].filter(Boolean).join(', '),
              suburb: a.suburb || f.suburb,
              state: a.state && AU_STATES.includes(a.state) ? a.state : f.state,
              postcode: a.postcode || f.postcode,
            }))} />
        </Field>
        <FormRow cols={3}>
          <Field label="Suburb">{(id) => <input id={id} className="input-field" value={form.suburb} onChange={(e) => set('suburb', e.target.value)} />}</Field>
          <Field label="State">
            {(id) => (
              <select id={id} className="input-field" value={form.state} onChange={(e) => set('state', e.target.value)}>
                {AU_STATES.map((s) => <option key={s}>{s}</option>)}
              </select>
            )}
          </Field>
          <Field label="Postcode">{(id) => <input id={id} className="input-field tabular-nums" inputMode="numeric" value={form.postcode} onChange={(e) => set('postcode', e.target.value)} />}</Field>
        </FormRow>
        <FormRow>
          <Field label="Phone">{(id) => <input id={id} type="tel" className="input-field" value={form.phone} onChange={(e) => set('phone', e.target.value)} />}</Field>
          <Field label="Email">{(id) => <input id={id} type="email" className="input-field" value={form.email} onChange={(e) => set('email', e.target.value)} />}</Field>
        </FormRow>
        <Field label="Contact name">{(id) => <input id={id} className="input-field" value={form.contactName} onChange={(e) => set('contactName', e.target.value)} />}</Field>
        <Field label="Notes">{(id) => <textarea id={id} className="input-field min-h-[70px] resize-y" value={form.notes} onChange={(e) => set('notes', e.target.value)} />}</Field>
        {isEdit && (
          <div className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 px-4 py-2.5">
            <span className="text-[13px] font-medium text-slate-800">Active</span>
            <Switch checked={form.isActive} onChange={(v) => set('isActive', v)} label="Active" />
          </div>
        )}
      </form>
    </Dialog>
  );
}

function FacilityCard({ f, onOpen }) {
  const address = formatAddress(f);
  return (
    <button type="button" onClick={() => onOpen(f)}
      className="card p-4 text-left w-full hover:border-slate-300 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
      <span className="flex items-start justify-between gap-3">
        <span className="font-semibold text-slate-900">{f.name}</span>
        <StatusPill status={f.is_active ? 'active' : 'inactive'} />
      </span>
      {f.facility_type && <span className="block text-[12.5px] text-slate-500 mt-0.5">{f.facility_type}</span>}
      <span className="block space-y-1.5 mt-3">
        {address && <span className="flex items-start gap-2 text-[12.5px] text-slate-600"><MapPin size={13} className="shrink-0 mt-0.5 text-slate-400" /><span>{address}</span></span>}
        {f.phone && <span className="flex items-center gap-2 text-[12.5px] text-slate-600"><Phone size={13} className="shrink-0 text-slate-400" /><span>{f.phone}</span></span>}
        {f.email && <span className="flex items-center gap-2 text-[12.5px] text-slate-600 min-w-0"><Mail size={13} className="shrink-0 text-slate-400" /><span className="truncate">{f.email}</span></span>}
        {f.contact_name && <span className="flex items-center gap-2 text-[12.5px] text-slate-600"><User size={13} className="shrink-0 text-slate-400" /><span>{f.contact_name}</span></span>}
      </span>
    </button>
  );
}

export default function Facilities() {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [modal, setModal] = useState(null);
  const [filter, setFilter] = useState('active');
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    const p = new URLSearchParams();
    if (filter === 'active') p.set('active', 'true');
    if (filter === 'inactive') p.set('active', 'false');
    api.get(`/facilities?${p}`)
      .then((r) => setFacilities(Array.isArray(r.data) ? r.data : []))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const closeModal = useCallback(() => setModal(null), []);
  const saved = useCallback(() => { setModal(null); load(); }, [load]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return !q ? facilities : facilities.filter((f) => [f.name, f.suburb, f.facility_type, f.contact_name, f.address]
      .some((v) => String(v || '').toLowerCase().includes(q)));
  }, [facilities, search]);

  const columns = [
    {
      key: 'name', header: 'Name', sortable: true,
      render: (f) => (
        <button type="button" onClick={(e) => { e.stopPropagation(); setModal(f); }}
          className="font-semibold text-slate-900 hover:text-primary-700 hover:underline underline-offset-2 text-left">{f.name}</button>
      ),
    },
    { key: 'facility_type', header: 'Type', sortable: true, render: (f) => f.facility_type || <span className="text-slate-500">—</span> },
    { key: 'address', header: 'Address', sortable: true, sortValue: (f) => formatAddress(f).toLowerCase() || null, render: (f) => formatAddress(f) || <span className="text-slate-500">—</span> },
    { key: 'phone', header: 'Phone', render: (f) => <span className="whitespace-nowrap tabular-nums">{f.phone || '—'}</span> },
    {
      key: 'contact_name', header: 'Contact', sortable: true,
      render: (f) => (f.contact_name || f.email ? (
        <div className="leading-tight">
          <div>{f.contact_name || '—'}</div>
          {f.email && <div className="text-[12px] text-slate-500 truncate max-w-[16rem]">{f.email}</div>}
        </div>
      ) : <span className="text-slate-500">—</span>),
    },
    { key: 'is_active', header: 'Status', sortable: true, sortValue: (f) => (f.is_active ? 0 : 1), render: (f) => <StatusPill status={f.is_active ? 'active' : 'inactive'} /> },
  ];

  const countText = loading ? 'Loading…' : search.trim() ? `${num(shown.length)} of ${plural(facilities.length, 'location')}` : plural(facilities.length, 'location');
  const empty = facilities.length === 0
    ? <EmptyState icon={Building2} title={filter === 'inactive' ? 'No inactive facilities' : 'No facilities yet'}
        hint={filter === 'inactive' ? undefined : 'Add the day programs, homes and sites your team works at.'}
        action={filter === 'inactive' ? undefined : <button type="button" onClick={() => setModal({})} className="btn-primary btn-sm"><Plus size={14} /> Add facility</button>} />
    : <EmptyState icon={Building2} title="No facilities match that search" action={<button type="button" onClick={() => setSearch('')} className="btn-secondary btn-sm">Clear search</button>} />;

  return (
    <div className={PAGE_WIDE}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center min-w-0">
          <Tabs variant="segmented" ariaLabel="Show facilities" value={filter} onChange={setFilter}
            tabs={[{ id: 'all', label: 'All' }, { id: 'active', label: 'Active' }, { id: 'inactive', label: 'Inactive' }]} />
          <SearchInput value={search} onChange={setSearch} placeholder="Search name, suburb, type or contact" className="w-full sm:w-72" />
          <span className="text-[13px] text-slate-500 tabular-nums whitespace-nowrap" aria-live="polite">{countText}</span>
        </div>
        <button type="button" onClick={() => setModal({})} className="btn-primary self-start lg:self-auto"><Plus size={16} /> Add facility</button>
      </div>

      {/* Wide screens: a table */}
      <div className="hidden lg:block bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loadError ? <ErrorState message="We couldn't load facilities. Check your connection, then try again." onRetry={load} />
          : loading ? <TableSkeleton rows={5} cols={6} />
          : shown.length === 0 ? empty
          : <DataTable label="Facilities" columns={columns} rows={shown} stickyHeader={false} onRowClick={(f) => setModal(f)} defaultSort={{ key: 'name', dir: 'asc' }} />}
      </div>

      {/* Smaller screens: cards */}
      <div className="lg:hidden">
        {loadError ? <div className="card"><ErrorState message="We couldn't load facilities. Check your connection, then try again." onRetry={load} /></div>
          : loading ? <div className="card"><TableSkeleton rows={4} cols={2} /></div>
          : shown.length === 0 ? <div className="card">{empty}</div>
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {shown.map((f) => <FacilityCard key={f.id} f={f} onOpen={setModal} />)}
            </div>
          )}
      </div>

      {modal !== null && <FacilityDialog facility={modal} onClose={closeModal} onSaved={saved} />}
    </div>
  );
}
